import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import Iconify from 'src/components/iconify';
import { useSelector } from 'react-redux';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings/selectors';
import { formatNumber, fPercent } from 'src/utils/format-number';
import { formatCurrency } from 'src/utils/format-currency';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomTooltip from 'src/components/visibility/components/CustomTooltip';

interface SeriesData {
    x: string;
    y: number;
}

interface Props {
    val: string;
    handleKPI: (key: string) => void;
    title: string;
    total: number;
    compare: number;
    color?: string;
    isPercent?: boolean;
    isCurrency?: boolean;
    series?: SeriesData[];
    definition?: string;
    isFetching?: boolean;
    selected: any,
    invertedColor?: boolean;
    isFirst?: boolean;
    sx?: any;
    palette?: { lighter?: string; darker?: string };
}

export default function BankingWidgetSummary({ ...props }: Props) {
    const { t, i18n } = useTranslation();
    const filterStore = useSelector((state: any) => state.filters);
    const {
        val, title, selected, total, compare, series, isPercent,
        isCurrency, definition, isFetching, isFirst = true,
        invertedColor, sx, handleKPI, ...otherProps
    } = props;
    const workspace: any = useCurrentWorkspace();
    const theme = useTheme();
    const lang = i18n.language || 'en';
    const currency = workspace?.currency || 'USD';


    const formatValue = (value: number, isPercentFlag: boolean) => {
        return formatTitle(title, value);
    };

    const compareValue = (totalVal: number, compareVal: number) => {
        if (compareVal == null) return undefined;
        // A compare period without data arrives as exactly 0: keep the badge (±∞)
        // instead of dropping it, like FunnelKpiCard.
        if (compareVal === 0) return totalVal === 0 ? 0 : totalVal > 0 ? Infinity : -Infinity;
        return ((totalVal - compareVal) / compareVal) * 100;
    };

    const formatTitle = (titleParam: any, totalParam: any) => {
        switch (titleParam.toLowerCase()) {
            case t('dashboard.global.ad_cost').toLowerCase():
            case t('dashboard.global.search_ad_cost').toLowerCase():
            case t('dashboard.global.shopping_ad_cost').toLowerCase():
            case t('dashboard.global.pmax_ad_cost').toLowerCase():
                return formatCurrency(totalParam, currency, lang, true, 1);
            case t('dashboard.brandTraffic.blendedCtr').toLowerCase():
            case t('dashboard.global.organic_ctr').toLowerCase():
            case t('dashboard.global.paid_ctr').toLowerCase():
            case t('dashboard.global.shopping_paid_ctr').toLowerCase():
            case t('dashboard.global.search_paid_ctr').toLowerCase():
            case t('dashboard.global.pmax_paid_ctr').toLowerCase():
            case "search impression share":
            case "impr. (top) %":
            case "impr. (abs. top)%".toLowerCase():
            case "search lost top is (rank)".toLowerCase():
                const cappedTotal = Math.min(totalParam, 100);
                return cappedTotal === 0 ? '0.0%' : fPercent(cappedTotal);
            case t('dashboard.brandTraffic.brandSearches').toLowerCase():
            case t('dashboard.brandTraffic.totalBrandTraffic').toLowerCase():
            case t('dashboard.brandTraffic.organic_brand_traffic').toLowerCase():
            case t('dashboard.brandTraffic.ad_brand_traffic').toLowerCase():
            case t('dashboard.global.search_brand_traffic').toLowerCase():
            case t('dashboard.global.shopping_brand_traffic').toLowerCase():
            case t('dashboard.global.pmax_brand_traffic').toLowerCase():
                return formatNumber(totalParam);
            case t('dashboard.global.blended_cpc').toLowerCase():
            case t('dashboard.global.cpc').toLowerCase():
            case t('dashboard.global.search_cpc').toLowerCase():
            case t('dashboard.global.shopping_cpc').toLowerCase():
            case t('dashboard.global.pmax_cpc').toLowerCase():
                return formatCurrency(totalParam, currency, lang, false, 2);
            default:
                return totalParam >= 1
                    ? formatNumber(totalParam)
                    : formatNumber(totalParam * 1000);
        }
    };

    // Définition de toutes les définitions possibles
    const KPI_DEFINITIONS: Record<string, string> = {
        "brand_searches": t('definitions.brand_searches'),
        "total_brand_traffic": t('definitions.brand_traffic'),
        "organic_brand_traffic": t('definitions.organic_brand_traffic'),
        "ad_brand_traffic": t('definitions.paid_brand_traffic'),
        "blended_ctr": t('definitions.blended_ctr'),
        "uncaptured_brand_traffic": t('definitions.uncaptured_brand_traffic'),
        "paid_ctr": t('definitions.paid_ctr'),
        "search_paid_ctr": t('definitions.search_paid_ctr'),
        "organic_ctr": t('definitions.organic_ctr'),
        "uncaptured_percentage": t('definitions.uncaptured_percentage'),
        "blended_cpc": t('definitions.blended_cpc'),
        "cpc": t('definitions.cpc'),
        "search_cpc": t('definitions.search_cpc'),
        "ad_cost": t('definitions.ad_cost'),
        "search_ad_cost": t('definitions.search_ad_cost'),
        "shopping_paid_ctr": t('definitions.shopping_paid_ctr'),
        "shopping_ad_cost": t('definitions.shopping_ad_cost'),
        "shopping_cpc": t('definitions.shopping_cpc'),
    };

    const COST_KPIS = [
        'cpc',
        'blended_cpc',
        'search_cpc',
        'shopping_cpc',
        'ad_cost',
        'search_ad_cost',
        'shopping_ad_cost',
    ];

    const isCostKPI = COST_KPIS.includes(val);

    // Évolution période cible vs comparaison. Texte coloré (pas de flèche), grisé si la
    // carte n'est pas sélectionnée. Pour les KPIs de coût, la hausse est "mauvaise" (rouge).
    const diff = compareValue(total, compare);
    const getChangeColor = () => {
        if (diff === undefined || isNaN(diff) || diff === 0) return '#637381';
        if (diff < 0) return isCostKPI ? theme.palette.success.main : theme.palette.error.main;
        return isCostKPI ? theme.palette.error.main : theme.palette.success.main;
    };
    const formatDiff = () => {
        if (diff === undefined || isNaN(diff)) return '+∞';
        if (!isFinite(diff)) return diff > 0 ? '+∞' : '-∞';
        if (diff === 0) return fPercent(0);
        return diff > 0 ? `+${fPercent(diff)}` : fPercent(diff);
    };

    return (
        <Stack
            onClick={() => handleKPI(val)}
            sx={{
                position: 'relative',
                width: 1,
                borderTopLeftRadius: isFirst ? "12px" : 0,
                borderBottomLeftRadius: isFirst ? "12px" : 0,
                // Compare mode stacks a second value + two date labels, which overflow the
                // 80px card and clip the bottom date label. Give it room so the bottom breathes.
                height: filterStore?.compare ? 100 : 80,
                overflow: 'hidden',
                border: '1px solid #EAEAEA',
                backgroundColor: '#FFF',
                ...sx,
            }}
        >
            <Box sx={{ width: 1, height: 1, padding: '12px 16px', position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Stack spacing={'6px'}>
                    <Box display={"flex"} flexDirection={'row'} justifyContent={"space-between"}>
                        <Box display="flex" flexDirection="row" alignItems="center" gap="6px">
                            <Typography
                                variant="body2"
                                sx={{ color: '#637381', fontSize: 12, lineHeight: '12px' }}
                            >
                                {title}
                            </Typography>
                            <CustomTooltip title={KPI_DEFINITIONS[val]} placement="top">
                                <Box component="span" sx={{ display: 'inline-flex', cursor: 'help', color: '#919EAB' }}>
                                    <Iconify icon="eva:info-outline" width={12} height={12} />
                                </Box>
                            </CustomTooltip>
                        </Box>

                        {filterStore.compare && diff !== undefined && (
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    fontWeight: 500,
                                    lineHeight: '14.4px',
                                    color: getChangeColor(),
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {formatDiff()}
                            </Typography>
                        )}
                    </Box>

                    {filterStore.compare ? (
                        <Box display="flex" flexDirection="column" gap="4px">
                            {/* Ligne valeurs : cible (sombre) + « vs. » + comparaison (grisée) */}
                            <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
                                <Typography sx={{ color: '#333C47', fontWeight: 700, fontSize: 20, lineHeight: 1 }}>
                                    {formatValue(total, isPercent ?? false)}
                                </Typography>
                                <Box display="flex" flexDirection="row" alignItems="baseline" gap="6px">
                                    <Typography sx={{ fontSize: 11, fontWeight: 400, lineHeight: '12px', color: '#919EAB' }}>vs.</Typography>
                                    <Typography sx={{ color: '#919EAB', fontWeight: 700, fontSize: 20, lineHeight: 1 }}>
                                        {formatValue(compare, isPercent ?? false)}
                                    </Typography>
                                </Box>
                            </Box>
                            {/* Ligne dates : période cible (gauche) / période de comparaison (droite) */}
                            <Box display="flex" flexDirection="row" justifyContent="space-between">
                                <Typography sx={{ fontSize: 11, fontWeight: 400, lineHeight: '12px', color: '#919EAB' }}>
                                    {filterStore?.start && filterStore?.end
                                        ? `${new Date(filterStore.start).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })} - ${new Date(filterStore.end).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })}`
                                        : ''}
                                </Typography>
                                <Typography sx={{ fontSize: 11, fontWeight: 400, lineHeight: '12px', color: '#919EAB' }}>
                                    {filterStore?.compareStart && filterStore?.compareEnd
                                        ? `${new Date(filterStore.compareStart).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })} - ${new Date(filterStore.compareEnd).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })}`
                                        : ''}
                                </Typography>
                            </Box>
                        </Box>
                    ) : (
                        <Typography variant="h4" sx={{ color: '#333C47', fontWeight: 700, fontSize: 20, lineHeight: '24px' }}>
                            {formatValue(total, isPercent ?? false)}
                        </Typography>
                    )}
                </Stack>
            </Box>
        </Stack>
    );
}
